import { Typography, Modal, Space, QRCode } from 'antd';
import { CodeInput } from 'shared/ui';
import { useIntl } from 'react-intl';
import { UpdateParams } from '../model/types';
import { useEffect, useRef, useState } from 'react';
import { ValidationType, ValidationUpdateStatus, ValidationMethod } from 'entities/user/model/types';
import { useUpdateActions } from '../api/actions';
import { useDispatch } from 'react-redux';
import { setActivity } from 'shared/config/store/reducers/appSlice';
import { useMessage } from 'shared/libs/hooks/useMessage/useMessage';
import { handleErrorWithCommonFallback } from 'shared/libs/utils/helpers';
import { setValidateConfirmation, setValidateLogin } from 'shared/config/store/reducers/userSlice';
import classes from './TwoFactorUpdate.module.css';

const { Text } = Typography;

type TwoFactorUpdateProps = {
  isOpen: boolean;
  email: string;
  updateParams?: UpdateParams;
  onClose: () => void;
};

export const TwoFactorUpdate = ({
  isOpen,
  email,
  updateParams,
  onClose,
}: TwoFactorUpdateProps) => {
  const { formatMessage: t } = useIntl();
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState<ValidationUpdateStatus | undefined>(
    updateParams?.status,
  );
  const { showError, showSuccess } = useMessage();
  const smsCodeRef = useRef<any>(null);
  const twoFactorCodeRef = useRef<any>(null);
  const updateActions = useUpdateActions();
  
  const identifier = `WestStein (${email})`;
  const provisionUrl = `otpauth://totp/${identifier}?secret=${
    updateParams?.secret || ''
  }`;
  const key = updateParams?.secret;

  const onSetLoginValidation = (method: ValidationMethod) => {
    dispatch(setValidateLogin(method));
  };
  const onSetConfirmationValidation = (method: ValidationMethod) => {
    dispatch(setValidateConfirmation(method));
  };

  const isTwoFactorStep =
    currentStep === ValidationUpdateStatus.NeedTwoFAConfirmation;
  const isSMSStep = currentStep === ValidationUpdateStatus.NeedSMSConfirmation;

  const handleGranted = () => {
    if (updateParams?.type === ValidationType.Login) {
      onSetLoginValidation(updateParams.method);
    }
    if (updateParams?.type === ValidationType.Confirmation) {
      onSetConfirmationValidation(updateParams.method);
    }
  };

  const onResendSms = async () => {
    dispatch(setActivity(true));
    try {
      await updateActions.resend(updateParams?.id || '');
      smsCodeRef.current?.clear();
      showSuccess(t({ id: 'common.otp.sent' }));
    } catch (error: any) {
      handleErrorWithCommonFallback(error.response, t, showError);
    } finally {
      dispatch(setActivity(false));
    }
  };

  const onEnterSmsCode = async (code: string) => {
    dispatch(setActivity(true));
    try {
      const result = await updateActions.confirm(updateParams?.id || '', { smsCode: code });
      if ('data' in result) {
        const { data } = result;
        smsCodeRef.current?.clear();
        if (data.response.status === ValidationUpdateStatus.NeedTwoFAConfirmation) {
          setCurrentStep(data.response.status);
        }
        if (data.response.status === ValidationUpdateStatus.Granted) {
          showSuccess(t({ id: 'screens.validation.message.success' }));
          handleGranted();
          onClose();
        }
      }
    } catch (error: any) {
      handleErrorWithCommonFallback(error.response, t, showError);
      smsCodeRef.current?.clear();
    } finally {
      dispatch(setActivity(false));
    }
  };

  const onEnterTwoFactorCode = async (code: string) => {
    dispatch(setActivity(true));
    try {
      const result = await updateActions.confirm(updateParams?.id || '', { twoFactorCode: code });
      if ('data' in result) {
        const { data } = result;
        twoFactorCodeRef.current?.clear();
        if (data.response.status === ValidationUpdateStatus.Granted) {
          showSuccess(t({ id: 'screens.validation.message.success' }));
          handleGranted();
          onClose();
        }
      }
    } catch (error: any) {
      twoFactorCodeRef.current?.clear();
      handleErrorWithCommonFallback(error.response, t, showError);
    } finally {
      dispatch(setActivity(false));
    }
  };

  useEffect(() => {
    if (updateParams && updateParams.status) {
      setCurrentStep(updateParams.status);
    }
  }, [updateParams]);

  if (!isOpen) return null;

  return (
    <Modal 
      title="&nbsp;" 
      footer={false} 
      centered 
      open={isOpen} 
      onCancel={onClose}
      width="auto"
      style={{ maxWidth: '90vw' }}
      classNames={{
        body: classes.twoFactorModalBody,
      }}
    >
      {isSMSStep && (
        <div>
          <Space
            align="center"
            direction="vertical"
            size={36}
            className={classes.spaceContainer}>
            <Text 
              className={classes.textContainer}
              style={{ 
                fontSize: 18, 
                fontWeight: 600,
              }}
            >
              {t({ id: 'common.otp.sent' })}
            </Text>
            <CodeInput onResend={onResendSms} ref={smsCodeRef} onEnter={onEnterSmsCode} />
          </Space>
        </div>
      )}
      {isTwoFactorStep && (
        <div>
          <Space
            align="center"
            direction="vertical"
            size={40}
            className={classes.spaceContainer}>
            {updateParams?.secret ? (
              <>
                <Text 
                  className={classes.textContainer}
                  style={{ 
                    fontSize: 18, 
                    fontWeight: 600,
                  }}
                >
                  {t({ id: 'profile.settings.2fa.scan' })}
                </Text>
                <div className={classes.qrCodeContainer}>
                  <div className={classes.qrCodeWrapper}>
                    <QRCode 
                      value={provisionUrl} 
                      size={256}
                    />
                  </div>
                </div>
                <Text copyable>{key}</Text>
              </>
            ) : (
              <Text 
                className={classes.textContainer}
                style={{ 
                  fontSize: 18, 
                  fontWeight: 600,
                }}
              >
                {t({ id: 'screens.auth.two.factor.legend' })}
              </Text>
            )}
            <CodeInput ref={twoFactorCodeRef} onEnter={onEnterTwoFactorCode} />
          </Space>
        </div>
      )}
    </Modal>
  );
}; 